import { ACTION_TYPE } from "../core/redux/actionType";
import { buildMultiContextDeleteRequest } from "./accessRules/requests/accessRuleRequestBuilder";
import { getRequestUtcDateTime } from "./accessRules/requests/accessRuleRequestMapper";
import { bulkDeleteLiftAccessRuleSetsAction } from "./bulkDeleteLiftAccessRuleSetsAction";
import {
  LiftAccessRuleSet,
  ruleSetContext,
} from "./accessRules/liftAccessRuleSet/models";
import {
  countOutcomes,
  extractApiError,
  extractResultsArray,
  reconcileResults,
} from "./accessRules/liftAccessRuleSet/bulkResults";
import {
  LiftAccessRuleBatchSaveResult,
  LiftAccessRuleSaveResult,
} from "./models";

/** Cancel only lands between chunks, so a chunk is the minimum overshoot. UX, not locking. */
const DEFAULT_CHUNK_SIZE = 200;

/** The endpoint's own per-call cap (MaxBulkDeleteContextsPerRequest). */
const MAX_CHUNK_SIZE = 500;

/**
 * Rules per call, not contexts, is what keeps the delete under the lock escalation
 * threshold. Raise it only against measured lock counts.
 */
const DEFAULT_RULE_BUDGET = 1500;

/** Assumed cost of a rule set whose rules were not loaded. See ruleCount. */
const TYPICAL_RULES_PER_SET = 4;

/** Delay between chunks, so a long run doesn't monopolise the API. */
const INTER_CHUNK_DELAY_MS = 100;

/** Safe because a chunk is atomic server-side and re-sending a deleted context is a no-op. */
const MAX_CHUNK_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 250;

/**
 * Transient conditions worth re-sending the whole chunk for. Deadlocks and timeouts
 * do NOT reach here — the API answers those as a 400 carrying per-context results,
 * which counts as a processed chunk and is retried from the failures screen instead.
 */
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

/**
 * A timeout carries no status, so status alone would never retry it. But only when the
 * request errored: a 2xx with no results is a contract violation, and retrying repeats it.
 */
const isRetryable = (
  dispatched: any,
  errorStatus: number | undefined,
): boolean =>
  !!dispatched?.error &&
  (errorStatus === undefined || RETRYABLE_STATUSES.has(errorStatus));

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Zero means the rules weren't loaded, not that the set is empty, so don't cost it zero. */
const ruleCount = (ruleSet: LiftAccessRuleSet): number => {
  const counted =
    (ruleSet.effectiveDateRangeRules?.length ?? 0) +
    (ruleSet.maxDaysRules?.length ?? 0) +
    (ruleSet.blackoutDateRangeRules?.length ?? 0) +
    (ruleSet.blackoutDayOfWeekRules?.length ?? 0);

  return counted > 0 ? counted : TYPICAL_RULES_PER_SET;
};

/** A rule set over the budget still goes, alone: the budget bounds calls, not what's deleted. */
export const buildDeleteChunks = (
  ruleSets: LiftAccessRuleSet[],
  maxContexts: number,
  ruleBudget: number,
): LiftAccessRuleSet[][] => {
  const chunks: LiftAccessRuleSet[][] = [];
  let current: LiftAccessRuleSet[] = [];
  let currentRules = 0;

  for (const ruleSet of ruleSets) {
    const rules = ruleCount(ruleSet);
    const full =
      current.length >= maxContexts || currentRules + rules > ruleBudget;

    if (current.length > 0 && full) {
      chunks.push(current);
      current = [];
      currentRules = 0;
    }

    current.push(ruleSet);
    currentRules += rules;
  }

  if (current.length > 0) chunks.push(current);

  return chunks;
};

/** The observed distribution is what sizes the rule budget. */
const logRuleDistribution = (
  ruleSets: LiftAccessRuleSet[],
  chunkCount: number,
  requestUtcDateTime: string,
) => {
  if (ruleSets.length === 0) return;

  const counts = ruleSets.map(ruleCount).sort((a, b) => a - b);
  const total = counts.reduce((sum, n) => sum + n, 0);

  console.info("Bulk delete sizing", {
    // Quote this with the user when reporting a run — it identifies it in the audit.
    runUtc: requestUtcDateTime,
    ruleSets: ruleSets.length,
    totalRules: total,
    minRules: counts[0],
    medianRules: counts[Math.floor(counts.length / 2)],
    maxRules: counts[counts.length - 1],
    chunks: chunkCount,
  });
};

/**
 * Deletes the rule sets in chunks, reporting progress through bulkOperationProgress and
 * returning one result per rule set. Sequential: chunks lock the same tables, so running
 * them concurrently would only self-contend.
 */
export const bulkDeleteLiftAccessRulesChunked =
  (
    ruleSets: LiftAccessRuleSet[],
    options: {
      userId: string;
      operatorId: string;
      cancelRef?: { cancelled: boolean };
      chunkSize?: number;
      ruleBudget?: number;
    },
  ) =>
  async (dispatch: any): Promise<LiftAccessRuleBatchSaveResult> => {
    const cancelRef = options.cancelRef ?? { cancelled: false };
    const maxContexts = Math.min(
      Math.max(1, options.chunkSize ?? DEFAULT_CHUNK_SIZE),
      MAX_CHUNK_SIZE,
    );
    const ruleBudget = Math.max(1, options.ruleBudget ?? DEFAULT_RULE_BUDGET);

    const total = ruleSets.length;
    const chunks = buildDeleteChunks(ruleSets, maxContexts, ruleBudget);

    // Once per run, sent on every chunk: with UserId this is the run's identity in
    // the audit log and on the API's telemetry. Do not move it into the chunk loop.
    const requestUtcDateTime = getRequestUtcDateTime();

    logRuleDistribution(ruleSets, chunks.length, requestUtcDateTime);

    let processed = 0;
    let runningSuccessCount = 0;
    let runningFailureCount = 0;
    let stoppedReason: LiftAccessRuleBatchSaveResult["stoppedReason"];
    const allResults: LiftAccessRuleSaveResult[] = [];

    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS,
      payload: { current: 0, total, successCount: 0, failureCount: 0 },
    });

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
      if (cancelRef.cancelled) break;

      const chunk = chunks[chunkIndex];
      const contexts = chunk.map(ruleSetContext);

      let perContext: unknown[] | undefined;
      let attempt = 0;
      // Hoisted so the handling below can tell a processed request from one that
      // never landed.
      let dispatched: any;

      while (attempt < MAX_CHUNK_ATTEMPTS) {
        attempt += 1;

        dispatched = await dispatch(
          bulkDeleteLiftAccessRuleSetsAction(
            buildMultiContextDeleteRequest({
              userId: options.userId,
              operatorId: options.operatorId,
              contexts,
              requestUtcDateTime,
            }),
          ),
        );

        // undefined = the middleware bailed out (breaker open), so nothing was sent.
        // Counting that as deleted would report rule sets gone that were never touched.
        const wasSent = !!dispatched && typeof dispatched === "object";

        if (!wasSent) {
          stoppedReason = { notSent: true };
          break;
        }

        perContext = extractResultsArray(dispatched);
        if (perContext) {
          stoppedReason = undefined;
          break;
        }

        const { errorStatus, errorMessage } = extractApiError(dispatched);
        stoppedReason = { errorStatus, errorMessage };

        if (
          !isRetryable(dispatched, errorStatus) ||
          attempt >= MAX_CHUNK_ATTEMPTS ||
          cancelRef.cancelled
        ) {
          break;
        }

        console.warn(
          `Bulk delete chunk ${chunkIndex + 1} failed (attempt ${attempt}), retrying`,
          stoppedReason,
        );

        await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));

        // Cancel can land during the backoff; without this the retry still fires.
        if (cancelRef.cancelled) break;
      }

      // A 2xx with no results array fails this chunk only: the request was processed,
      // so later chunks still stand. Its rule sets count as unanswered, never as
      // deleted. Still reachable against an API old enough to answer 204.
      const wasProcessed =
        !!dispatched && typeof dispatched === "object" && !dispatched.error;

      if (!perContext && wasProcessed) {
        console.warn(
          `Bulk delete chunk ${chunkIndex + 1}: response carried no results array`,
        );
        perContext = [];
        stoppedReason = undefined;
      }

      // The whole request failed, so nothing in the chunk was examined: report it once
      // as unattempted, not as a chunk of individual failures, and stop since later
      // chunks fail the same way. Never infer success from an empty body.
      if (!perContext) {
        console.warn(
          "Bulk delete stopped: request failed before any rule set was processed",
          stoppedReason,
        );
        break;
      }

      const chunkResults = reconcileResults(contexts, perContext);
      const counts = countOutcomes(chunkResults);

      allResults.push(...chunkResults);
      runningSuccessCount += counts.successCount;
      runningFailureCount += counts.failureCount;

      // In rule sets, not requests: matches what the user selected.
      processed += chunk.length;

      dispatch({
        type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS,
        payload: {
          current: processed,
          total,
          successCount: runningSuccessCount,
          failureCount: runningFailureCount,
        },
      });

      if (!cancelRef.cancelled && chunkIndex + 1 < chunks.length) {
        await delay(INTER_CHUNK_DELAY_MS);
      }
    }

    const combinedResult: LiftAccessRuleBatchSaveResult = {
      results: allResults,
      successCount: runningSuccessCount,
      failureCount: runningFailureCount,
      hasFailures: runningFailureCount > 0,
      cancelled: cancelRef.cancelled,
      // A chunk that failed as a whole never advanced `processed`, so its rule sets
      // land here rather than in failureCount.
      notAttempted: Math.max(total - processed, 0),
      stoppedReason,
      runUtcDateTime: requestUtcDateTime,
    };

    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE,
      payload: { ...combinedResult, cancelled: cancelRef.cancelled },
    });

    return combinedResult;
  };
